import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Stack, useTheme, useMediaQuery } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { menuOutlinedBoxButtonSx } from '../../utils/landingThemeHelpers';

/**
 * Shared public top menu:
 * Home · Sign In / Login · Register / Signup · Browsing Page
 * (or Home · Dashboard · Logout · Browsing when signed in).
 * Active page uses a bright sparkle box; labels are centered.
 */
export default function PublicSiteAccessButtons({
  active = 'home',
  size = 'medium',
  signupEnabled = true,
  hideBrowsing = false
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const compact = useMediaQuery(theme.breakpoints.down('md'));
  const btnSize = size === 'small' || compact ? 'small' : 'medium';

  const goLogin = (entryLabel, redirectPath) => {
    navigate('/login', {
      state: { from: { pathname: redirectPath }, entryLabel },
      replace: active === 'signin'
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const boxSx = (key, tone) => menuOutlinedBoxButtonSx({ active: active === key, tone, uniform: true });

  return (
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap alignItems="center">
      <Button
        variant="outlined"
        size={btnSize}
        component={RouterLink}
        to="/"
        sx={boxSx('home', 'home')}
      >
        Home
      </Button>

      {!isAuthenticated ? (
        <>
          <Button
            variant="outlined"
            size={btnSize}
            onClick={() => goLogin('Sign In / Login', '/dashboard')}
            sx={boxSx('signin', 'signin')}
          >
            {compact ? 'Sign In' : 'Sign In / Login'}
          </Button>
          {signupEnabled ? (
            <Button
              variant="outlined"
              size={btnSize}
              component={RouterLink}
              to="/signup"
              sx={boxSx('register', 'register')}
            >
              {compact ? 'Register' : 'Register / Signup'}
            </Button>
          ) : null}
        </>
      ) : (
        <>
          <Button
            variant="outlined"
            size={btnSize}
            component={RouterLink}
            to="/dashboard"
            sx={boxSx('dashboard', 'dashboard')}
          >
            Dashboard
          </Button>
          <Button
            variant="outlined"
            size={btnSize}
            onClick={handleLogout}
            sx={boxSx('logout', 'logout')}
          >
            Logout
          </Button>
        </>
      )}

      {!hideBrowsing ? (
        <Button
          variant="outlined"
          component={RouterLink}
          to="/query"
          size={btnSize}
          sx={boxSx('query', 'browsing')}
        >
          {compact ? 'Browsing' : 'Browsing Page'}
        </Button>
      ) : null}
    </Stack>
  );
}
